import { useEffect, useId, useState } from "react";

import Close from "@mui/icons-material/Close";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Search from "@mui/icons-material/Search";
import {
  Box,
  IconButton,
  InputBase,
  Link,
  Paper,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const searchExamples = [
  { query: "black shirt", descriptionKey: "searchTipsWords" },
  { query: '"black shirt"', descriptionKey: "searchTipsPhrase" },
  { query: "black -shirt", descriptionKey: "searchTipsExclude" },
  { query: "black OR shirt", descriptionKey: "searchTipsOr" },
] as const;

// Decoupled search input for RubiALERT. Owns the draft text internally and
// commits it to the parent via onSearch (Enter) / onClear. The parent owns the
// committed search state (URL param) and any side effects (loading, list reset,
// scroll). Mirrors the future @rubi/ui SearchBar API so this can be swapped for
// the shared component later.
interface AlertSearchInputProps {
  searchText: string;
  loading: boolean;
  onSearch: (text: string) => void;
  onClear: () => void;
}

const AlertSearchInput = ({
  searchText,
  loading,
  onSearch,
  onClear,
}: AlertSearchInputProps) => {
  const [draft, setDraft] = useState(searchText || "");
  const [helpAnchor, setHelpAnchor] = useState<HTMLButtonElement | null>(null);
  const helpId = useId();
  const helpTitleId = `${helpId}-title`;
  const { t } = useTranslation("bulletin/searchbar");

  const closeHelp = () => setHelpAnchor(null);

  const handleClear = () => {
    setDraft("");
  };

  const handleEnter = () => {
    if (draft !== "") {
      onSearch(draft);
    }
  };

  // Keep local draft in sync with the committed value (e.g. back/forward nav).
  useEffect(() => {
    setDraft(searchText || "");
  }, [searchText]);

  // Clear the committed search when the field is emptied (but not mid-load).
  useEffect(() => {
    if (draft === "" && searchText !== "" && !loading) {
      onClear();
    }
  }, [draft, loading, onClear, searchText]);

  return (
    <Paper
      component="form"
      elevation={4}
      sx={{
        p: "2px 4px",
        display: "flex",
        alignItems: "center",
        width: "100%",
        borderRadius: 1,
        zIndex: 4,
        bgcolor: "var(--search-field-background)",
        // Paper paints a white elevation overlay in dark mode
        // (backgroundImage: var(--Paper-overlay)) that stacks on top of bgcolor
        // and overrides the token. The elevation={4} shadow is unaffected.
        backgroundImage: "none",
        // Outlined in dark mode (designer's shell pass); transparent in light
        // so the field is the same size in both.
        border: "1px solid var(--search-field-border)",
      }}
    >
      <IconButton
        type="button"
        sx={{ p: "10px", color: "text.secondary" }}
        aria-label="search"
      >
        <Search />
      </IconButton>

      <InputBase
        sx={{
          ml: 1,
          flex: 1,
          minWidth: 0,
          fontSize: "16px",
          color: "text.primary",
          // MUI's default placeholder is currentColor at 0.5 opacity (dark) /
          // 0.42 (light), which fails WCAG AA against this pill in both modes.
          "& .MuiInputBase-input::placeholder": {
            color: "text.secondary",
            opacity: 1,
          },
        }}
        placeholder={t("search")}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            handleEnter();
          }
        }}
      />

      <Tooltip title={t("searchTips")} disableInteractive>
        <IconButton
          type="button"
          aria-label={t("searchTips")}
          aria-haspopup="dialog"
          aria-expanded={Boolean(helpAnchor)}
          aria-controls={helpAnchor ? helpId : undefined}
          sx={{ p: 1.25, color: "text.secondary" }}
          onClick={(event) => setHelpAnchor(event.currentTarget)}
        >
          <InfoOutlined />
        </IconButton>
      </Tooltip>

      <IconButton
        type="button"
        aria-label="clear"
        sx={{ p: "10px", color: "text.secondary" }}
        onClick={handleClear}
      >
        <Close />
      </IconButton>

      <Popover
        open={Boolean(helpAnchor)}
        anchorEl={helpAnchor}
        onClose={closeHelp}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 1 }}
        slotProps={{
          paper: {
            id: helpId,
            role: "dialog",
            "aria-modal": true,
            "aria-labelledby": helpTitleId,
            sx: (theme) => ({
              p: 2,
              width: theme.spacing(48),
              maxWidth: `calc(100vw - ${theme.spacing(4)})`,
            }),
          },
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography id={helpTitleId} variant="subtitle1" component="h2">
              {t("searchTips")}
            </Typography>
            <IconButton
              type="button"
              aria-label={t("closeSearchTips")}
              sx={{ p: 1.25 }}
              onClick={closeHelp}
            >
              <Close />
            </IconButton>
          </Stack>

          <Stack component="dl" spacing={1.5} sx={{ m: 0 }}>
            {searchExamples.map(({ query, descriptionKey }) => (
              <Box
                key={query}
                sx={(theme) => ({
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  columnGap: 2,
                  rowGap: 0.5,
                  "& > dt": { minWidth: theme.spacing(16) },
                  "& > dd": { flex: `1 1 ${theme.spacing(18)}` },
                })}
              >
                <Typography component="dt" variant="body2">
                  <Box
                    component="code"
                    sx={{
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      px: 0.5,
                      py: 0.25,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {query}
                  </Box>
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  color="text.secondary"
                  sx={{ m: 0 }}
                >
                  {t(descriptionKey)}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {t("searchTipsImages")}
          </Typography>
          <Link
            href="/rubialert/training#how-to-search-bulletins"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${t("watchSearchTutorial")} ${t("opensInNewTab")}`}
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
          >
            {t("watchSearchTutorial")}
            <OpenInNew fontSize="inherit" />
          </Link>
        </Stack>
      </Popover>
    </Paper>
  );
};

export default AlertSearchInput;
